import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, AlertTriangle } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

const PAIN_POINTS = [
  "Validation documentation backlogs delaying equipment release",
  "CAPA and deviation records that won't survive an FDA audit",
  "Lean teams without dedicated GxP documentation specialists",
  "Risk from ad hoc AI use without compliance guardrails",
  "High cost of traditional CRO or consulting engagement",
];

export default function PharmaGxPSupport() {
  return (
    <>
      <SEO
        title="GxP Documentation Support for Smaller Pharma & Regulated Teams | LabComplyx"
        description="LabComplyx provides selective GxP documentation support for smaller pharma and regulated teams — validation records, CAPA, deviation documentation — without enterprise overhead."
        canonical="https://labcomplyx.com/industries/pharma-gxp"
      />
      <div className="pt-24">
        <section className="pb-16 bg-background">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="inline-block text-xs font-semibold text-secondary bg-secondary/10 px-3 py-1 rounded-full mb-4">GxP · FDA · ICH</span>
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-5 leading-tight">
                Selective GxP documentation support — without the enterprise overhead
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                LabComplyx provides focused validation, CAPA, and deviation documentation support for smaller pharma and GxP-regulated teams. Not a QMS replacement. A documentation partner that fits your scale.
              </p>
              <Link to="/ValidationIntake">
                <Button size="lg" className="gap-2">
                  Request a GxP Consultation <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30 border-y border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">GxP documentation challenges for lean teams</h2>
                <ul className="space-y-3">
                  {PAIN_POINTS.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-muted-foreground">
                      <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-card border border-border rounded-xl p-7">
                <h3 className="font-semibold text-foreground mb-4">How LabComplyx supports GxP teams</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                  We provide selective documentation support that fills the gap between lean internal capacity and costly enterprise consulting. Every output is governed, traceable, and audit-ready.
                </p>
                <ul className="space-y-2.5">
                  {["IQ/OQ/PQ validation documentation", "CAPA and deviation records with full audit trail", "21 CFR Part 11 compliant e-signatures", "SOP drafting and lifecycle management", "Governed AI with human review checkpoint"].map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Not a LIMS statement + capabilities + blog links */}
        <section className="py-10 bg-background border-b border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="bg-muted/40 border border-border rounded-xl p-5 max-w-2xl mb-8">
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">LabComplyx is not a full GxP eQMS or LIMS replacement.</strong> It provides selective documentation support that fills the gap between lean internal capacity and costly enterprise consulting — without requiring a platform rollout or large-scale system change.
              </p>
            </div>
            <h2 className="text-xl font-bold text-foreground mb-4">Related capabilities</h2>
            <div className="flex flex-wrap gap-3 mb-6">
              {[
                { label: "CAPA Documentation", href: "/solutions/capa-documentation" },
                { label: "Validation Support", href: "/solutions/validation-support" },
                { label: "Deviation Documentation", href: "/solutions/deviation-documentation" },
                { label: "AI Governance", href: "/solutions/ai-governance" },
              ].map((c) => (
                <Link key={c.href} to={c.href} className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg border border-border bg-card hover:border-primary/40 hover:text-primary transition-colors">
                  {c.label} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              ))}
            </div>
            <h3 className="text-base font-semibold text-foreground mb-3">Relevant guides & resources</h3>
            <div className="flex flex-wrap gap-3">
              {[
                { label: "IQ/OQ/PQ Validation Guide", href: "/blog/iq-oq-pq-validation-guide" },
                { label: "CAPA Root Cause Analysis", href: "/blog/capa-root-cause-analysis" },
                { label: "GxP Compliance Guide for Small Labs", href: "/blog/gxp-compliance-guide-small-labs" },
              ].map((b) => (
                <Link key={b.href} to={b.href} className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg border border-border bg-muted/30 hover:border-primary/40 hover:text-primary transition-colors">
                  {b.label} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-2xl font-bold mb-4">Selective GxP support that fits your scale</h2>
            <p className="text-primary-foreground/80 mb-8">Request a consultation to discuss your validation backlog, CAPA gaps, or deviation documentation needs.</p>
            <Link to="/ValidationIntake">
              <Button size="lg" variant="secondary" className="gap-2">
                Request a GxP Consultation <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}